import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as classes from './LoansCalculator.module.scss';
import { convertNumberToCurrency } from '../../../src/utils.js';

const configurations = {
  personal: {
    months: [
      { totalMonths: 12, rate: 1.06022 },
      { totalMonths: 24, rate: 1.11786 },
      { totalMonths: 36, rate: 1.17748 },
      { totalMonths: 48, rate: 1.23906 },
      { totalMonths: 60, rate: 1.3026 }
    ],
    repayments: { defaultTotal: 530.11, defaultMonthlyTotal: 44.18 },
    min: 500,
    max: 5000,
    rate: 11.5,
    amount: '2,000',
    total: '2,235.79',
    monthly: '93.16',
    month: '24'
  },
  business: {
    months: [
      { totalMonths: 12, rate: 1.037212 },
      { totalMonths: 24, rate: 1.072218 },
      { totalMonths: 36, rate: 1.10803 },
      { totalMonths: 48, rate: 1.144624 },
      { totalMonths: 60, rate: 1.181992 }
    ],
    repayments: { defaultTotal: 5186.06, defaultMonthlyTotal: 432.17 },
    min: 5000,
    max: 25000,
    rate: 7,
    amount: '15,000',
    total: '16,620.47',
    monthly: '461.68',
    month: '36'
  }
};

const LoansCalculator = ({ type, title }) => {
  const config = configurations[type];

  const [borrowAmount, setBorrowAmount] = useState(config.min);
  const [loanPeriod, setLoanPeriod] = useState(config.months[0]);
  const [repaymentAmounts, setRepaymentAmounts] = useState(config.repayments);
  const [isDragging, setIsDragging] = useState(false);
  const [validBorrowAmount, setValidBorrowAmount] = useState(true);

  useEffect(() => {
    if (!isDragging && validBorrowAmount) {
      setRepaymentAmounts({
        total: (borrowAmount * loanPeriod.rate).toFixed(2),
        monthly: ((borrowAmount * loanPeriod.rate) / loanPeriod.totalMonths).toFixed(2)
      });
    }
    if (config.min <= borrowAmount && borrowAmount <= config.max) {
      setValidBorrowAmount(true);
    } else {
      setValidBorrowAmount(false);
      setRepaymentAmounts({
        total: ' -',
        monthly: ' -'
      });
    }
  }, [borrowAmount, loanPeriod, isDragging, validBorrowAmount, config]);

  return (
    <section className={classes.loansCalculator} id={`loan-calculator-${type}`}>
      <h2 className={classes.loansCalculator_title}>{title}</h2>
      <noscript>
        <p>To use the calculator tool please enable Javascript</p>
      </noscript>

      <div className={classes.loansCalculator_slider_wrapper}>
        <p>Borrow</p>
        <br />
        <span>
          £
          <input
            min={config.min}
            max={config.max}
            step="1"
            type="number"
            value={borrowAmount}
            onChange={(event) => setBorrowAmount(event.target.value)}
            className={
              borrowAmount < config.min || borrowAmount > config.max
                ? `${classes.loansCalculator_borrow_amount} ${classes.loansCalculator_borrow_amount_error}`
                : classes.loansCalculator_borrow_amount
            }
          />
        </span>
        <br />
        <br />
        <input
          type="range"
          min={config.min}
          max={config.max}
          onChange={(event) => setBorrowAmount(event.target.value)}
          value={borrowAmount}
          className={classes.loansCalculator_slider}
          onTouchEnd={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
        />
        {(borrowAmount < config.min || borrowAmount > config.max) && (
          <p>
            <small>
              Please enter an amount between £{config.min} and £{config.max}
            </small>
          </p>
        )}
      </div>
      <div className={classes.loansCalculator_installments_wrapper}>
        <p>For</p>
        <div className={classes.loansCalculator_period_control}>
          {config.months.map((month) => {
            return (
              <button
                key={`${month.totalMonths} Months`}
                onClick={() => setLoanPeriod(month)}
                className={
                  loanPeriod === month
                    ? classes.loansCalculator_period_control_active_button
                    : classes.loansCalculator_period_control_inactive_button
                }
              >
                {month.totalMonths}
                <br />
                <span className={classes.loansCalculator_display_months}>Months</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={classes.loansCalculator_information_wrapper}>
        <p>over {loanPeriod.totalMonths} months</p>
        <div className={classes.loansCalculator_repayment_information_wrapper}>
          <div className={classes.loansCalculator_repayment_information}>
            <strong>Total Amount Repayable </strong>
            <span>
              {repaymentAmounts.total === ' -'
                ? '£ -'
                : convertNumberToCurrency(repaymentAmounts['total'], 'en-GB', 'GBP')}
            </span>
          </div>
          <div className={classes.loansCalculator_repayment_information}>
            <strong>Monthly Repayment </strong>
            <span>
              {repaymentAmounts.monthly === ' -'
                ? '£ -'
                : convertNumberToCurrency(repaymentAmounts['monthly'], 'en-GB', 'GBP')}
            </span>
          </div>

          <div className={classes.loansCalculator_repayment_information}>
            <strong>Representative APR</strong> {config.rate}%
          </div>
        </div>
      </div>

      <footer className={classes.loansCalculator_footer}>
        Indicative example based on loan starting 1 January 2020. Actual repayments may differ slightly depending on the
        start date of your loan.
        <br />
        <br />
        Representative example: <br /> Representative APR of {config.rate}%. Borrow £{config.amount} repayable over{' '}
        {config.month} months at an interest rate of {config.rate}% pa (fixed) with monthly repayments of £
        {config.monthly}. A total amount payable of £{config.total}.<br />
        The rate you are offered will depend on your individual{type === 'business' && ' business’'} circumstances.
      </footer>
    </section>
  );
};

LoansCalculator.propTypes = {
  type: PropTypes.oneOf(['personal', 'business']),
  customPadding: PropTypes.oneOf(['no-top', 'no-bottom', 'no-top-bottom']),
  title: PropTypes.string
};

export default LoansCalculator;
